import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  sessionActiveElsewhereDetails,
  SESSION_ACCOUNT_DEACTIVATED_MESSAGE,
  SESSION_ENDED_ELSEWHERE_MESSAGE,
} from '@cms/shared';
import { useAuth } from '../../lib/auth.js';
import { ApiClientError } from '../../lib/apiClient.js';
import { sessionAgeLabel } from './sessionAge.js';

/**
 * Login, styled to match opioid-demo's palette and card conventions (docs/STYLING.md).
 * opioid-demo renders its own login server-side, so this follows the brand rather than
 * copying markup that has no counterpart there.
 *
 * The error handling carries the opinion: whatever the API says, the user is told one
 * thing. The server already refuses to distinguish an unknown account from a wrong
 * password, and guessing more specifically here would hand back the enumeration oracle the
 * API just closed. Rate limiting and server failures are separated, because those say
 * something actionable about when to try again.
 *
 * ── The one exception, and why it is not an error ─────────────────────────────────────
 * A 409 saying the account is signed in elsewhere (docs/SINGLE-SESSION-PLAN.md §5). It arrives
 * through the same catch, and it is the only response here that is a question rather than a refusal:
 * the password was right, nothing was revoked, and the next request decides. So it renders as a
 * prompt with two buttons and not as a red alert — a red alert would read as "you are locked out",
 * which is the opposite of what happened.
 *
 * It is recognised by parsing `details` through the shared contract rather than by reading a field
 * off `unknown`. A 409 that does not match the contract falls through to the ordinary message, which
 * is the right failure: better one confusing sentence than a prompt whose "Continue" does nothing.
 */
export function LoginPage() {
  const { status, login, endedElsewhere, endedReason, acknowledgeEndedElsewhere } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** When the other session began, if the server has asked whether to replace it. Null otherwise. */
  const [activeElsewhereSince, setActiveElsewhereSince] = useState<string | null>(null);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (status === 'authenticated') return <Navigate to={from} replace />;

  async function attempt(replaceExistingSession: boolean) {
    setSubmitting(true);
    setError(null);
    /**
     * The notice is spent the moment they act on it.
     *
     * It explained how they arrived at this form. Left on screen beside "Invalid email or password"
     * it would be offering a second, contradictory explanation for the attempt they just made.
     */
    acknowledgeEndedElsewhere();

    try {
      await login(email, password, { replaceExistingSession });
      navigate(from, { replace: true });
    } catch (err) {
      /**
       * The prompt, before the error branches — because it is not one.
       *
       * Only when the caller did not already confirm: a 409 in answer to a confirmed submit would
       * mean the server asked twice, and re-showing the prompt would loop the user through a
       * question they have already answered.
       */
      const prompt =
        !replaceExistingSession && err instanceof ApiClientError && err.status === 409
          ? sessionActiveElsewhereDetails.safeParse(err.details)
          : undefined;

      if (prompt?.success) {
        setActiveElsewhereSince(prompt.data.startedAt);
      } else if (err instanceof ApiClientError && err.code === 'RATE_LIMITED') {
        setError('Too many attempts. Wait a few minutes and try again.');
      } else if (err instanceof ApiClientError && err.status >= 500) {
        setError(`Something went wrong on our side. Reference: ${err.requestId ?? 'unknown'}`);
      } else {
        setActiveElsewhereSince(null);
        setError('Invalid email or password.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await attempt(false);
  }

  /**
   * Editing either field retires the prompt.
   *
   * The question was about the account that was just submitted. Left on screen while somebody types
   * a different email, "Continue" would answer it for an account they are no longer signing into.
   */
  function onFieldChange(set: (value: string) => void) {
    return (value: string) => {
      setActiveElsewhereSince(null);
      set(value);
    };
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="text-center mb-4 login-brand">
          <img src="/blue-lot-logo.png" alt="BlueLot" />
        </div>

        <div className="card stat-card">
          <div className="card-body p-4">
            <h1 className="h5 mb-3">Sign in</h1>

            {/**
             * Why the session ended, when it ended because somebody signed in elsewhere
             * (docs/SINGLE-SESSION-PLAN.md §4a, slice 5).
             *
             * `alert-info` rather than `alert-danger`: nothing went wrong, and the user is not being
             * refused anything. They did this — on their phone, ten minutes ago — and the only thing
             * they need to know is that it is why they are looking at this form.
             *
             * Without it the app simply stops working mid-task and shows a login page, which reads as
             * a bug and gets reported as one.
             *
             * No `error === null` guard here, deliberately: `attempt` retires the notice before it does
             * anything, so by the time there is an error to show the flag is already false. A second
             * guard would make that call untestable — it would hide the notice whether or not the
             * acknowledgement worked, which is how a redundant mechanism becomes the only mechanism.
             */}
            {endedElsewhere ? (
              <div className="alert alert-info py-2 px-3 small" role="status">
                {SESSION_ENDED_ELSEWHERE_MESSAGE}
              </div>
            ) : null}
            {endedReason === 'deactivated' ? (
              <div className="alert alert-warning py-2 px-3 small" role="status">
                {SESSION_ACCOUNT_DEACTIVATED_MESSAGE}
              </div>
            ) : null}

            {error ? (
              <div className="alert alert-danger py-2 px-3 small" role="alert">
                {error}
              </div>
            ) : null}

            <form onSubmit={onSubmit} noValidate>
              <div className="mb-3">
                <label className="form-label small" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="form-control"
                  autoComplete="username"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => onFieldChange(setEmail)(e.target.value)}
                />
              </div>

              <div className="mb-3">
                <label className="form-label small" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  className="form-control"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => onFieldChange(setPassword)(e.target.value)}
                />
              </div>

              {activeElsewhereSince === null ? (
                <button type="submit" className="btn btn-primary w-100" disabled={submitting}>
                  {submitting ? 'Signing in…' : 'Sign in'}
                </button>
              ) : (
                /**
                 * In place of the Sign in button rather than above it.
                 *
                 * With both on screen the primary action is ambiguous — pressing Enter would repeat
                 * the request that produced the question. Here there is one way forward and one way
                 * back, and neither is the button that got them here.
                 *
                 * `role="status"`, not `role="alert"`: nothing has gone wrong. The heading is what a
                 * screen reader lands on, and the Continue button takes focus because the user's
                 * hands were on the keyboard a moment ago.
                 */
                <div className="alert alert-warning py-2 px-3 mb-0" role="status">
                  <p className="mb-1 fw-semibold">You’re already signed in somewhere else</p>
                  <p className="small mb-2">
                    That session started {sessionAgeLabel(activeElsewhereSince)}. Continuing will
                    sign it out — anything unsaved there will be lost.
                  </p>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      autoFocus
                      disabled={submitting}
                      onClick={() => void attempt(true)}
                    >
                      {submitting ? 'Signing in…' : 'Continue and sign out the other session'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={submitting}
                      onClick={() => setActiveElsewhereSince(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>

        <p className="text-center text-muted small mt-3 mb-0">
          &copy; {new Date().getFullYear()} BlueLot &middot; A perfect spot for clear data
        </p>
      </div>
    </div>
  );
}
